import { useEffect, useRef, useState } from 'react'
import { Bot, Send, Loader2 } from 'lucide-react'
import { Streamdown } from 'streamdown'
import 'streamdown/styles.css'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { CHAT_MARKDOWN_PROSE_CLASS } from '@/components/chat/markdown-prose-classes'
import { useDraftChat } from '@/lib/api/mutations/draft-chat'

interface DraftChatPanelProps {
  agentId: string
  draftId: string
}

/**
 * Phase 35-02 (DRAFT-01): chat panel embedded next to the edit-draft body
 * editor. Lets the user converse with the agent about the draft; if the
 * assistant returns a `<draft_edit>` block the API applies it, emits
 * `event: draftUpdated`, and the hook invalidates the draft detail
 * TanStack query — so `DraftBodyViewer` / `DraftBodyEditor` re-renders
 * automatically from the authenticated GET draft path.
 *
 * The existing draft state machine (edit / approve / reject / publish) is
 * untouched — this panel is a sibling of the body editor and only mutates
 * `blog_drafts.body` indirectly via the agent.
 */
export function DraftChatPanel({ agentId, draftId }: DraftChatPanelProps) {
  const { sendMessage, streamingText, isStreaming, messages, error } = useDraftChat({
    agentId,
    draftId,
  })
  const [input, setInput] = useState('')
  const [isSending, setIsSending] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)

  // Auto-scroll on new tokens or new messages
  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: 'smooth',
    })
  }, [messages.length, streamingText])

  // Surface stream errors as toasts (TanStack invalidation handled in hook)
  useEffect(() => {
    if (error) toast.error(error.length > 120 ? 'Chat failed. Please try again.' : `Chat failed: ${error}`)
  }, [error])

  const canSend = input.trim().length > 0 && !isSending && !isStreaming

  const handleSend = async () => {
    const content = input.trim()
    if (!content) return
    setIsSending(true)
    setInput('')
    try {
      const result = await sendMessage(content)
      if (result.draftUpdated) {
        toast.success('Agent edited the draft')
      }
    } catch {
      // Error already surfaced via the `error` effect above; restore input.
      setInput(content)
    } finally {
      setIsSending(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && canSend) {
      e.preventDefault()
      void handleSend()
    }
  }

  return (
    <div
      className="flex flex-col h-full min-h-[400px] bg-card border border-border rounded-lg overflow-hidden"
      data-testid="draft-chat-panel"
    >
      <div className="border-b border-border px-4 py-3">
        <h3 className="text-sm font-medium">Chat with agent about this draft</h3>
        <p className="text-xs text-muted-foreground mt-0.5">
          Ask the agent to revise the draft. Edits are applied directly to the body.
        </p>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4">
        {messages.length === 0 && !streamingText && !isStreaming && (
          <div className="flex flex-col items-center justify-center h-full text-center py-8">
            <Bot className="size-10 text-muted-foreground/50 mb-3" />
            <p className="text-sm text-muted-foreground max-w-xs">
              Suggest an edit, ask for a rewrite, or request a tone change.
            </p>
          </div>
        )}

        <div className="flex flex-col gap-4">
          {messages.map((msg, i) => (
            <div
              key={i}
              className={
                msg.role === 'user'
                  ? 'ml-auto max-w-[85%] min-w-0 flex flex-col items-end'
                  : 'mr-auto max-w-[85%] min-w-0 flex gap-2'
              }
            >
              {msg.role === 'assistant' && (
                <div className="flex-shrink-0 mt-1">
                  <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center">
                    <Bot className="w-3.5 h-3.5 text-primary" />
                  </div>
                </div>
              )}
              <div
                className={
                  msg.role === 'user'
                    ? 'bg-primary/10 rounded-2xl rounded-br-sm p-3 max-w-full overflow-hidden'
                    : 'bg-muted/40 border border-border rounded-2xl rounded-bl-sm p-3 max-w-full overflow-hidden flex-1 min-w-0'
                }
              >
                <div className={CHAT_MARKDOWN_PROSE_CLASS}>
                  {msg.role === 'user' ? <p>{msg.content}</p> : <Streamdown>{msg.content}</Streamdown>}
                </div>
              </div>
            </div>
          ))}

          {(streamingText || isStreaming) && (
            <div className="mr-auto max-w-[85%] min-w-0 flex gap-2">
              <div className="flex-shrink-0 mt-1">
                <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center">
                  <Bot className="w-3.5 h-3.5 text-primary" />
                </div>
              </div>
              <div className="bg-muted/40 border border-border rounded-2xl rounded-bl-sm p-3 max-w-full overflow-hidden flex-1 min-w-0">
                {streamingText ? (
                  <div className={CHAT_MARKDOWN_PROSE_CLASS}>
                    <Streamdown animated isAnimating={isStreaming}>
                      {streamingText}
                    </Streamdown>
                  </div>
                ) : (
                  <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="border-t border-border p-3">
        <div className="flex items-end gap-2">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={isStreaming ? 'Waiting for response...' : 'Ask the agent to revise...'}
            disabled={isStreaming}
            aria-label="Draft chat message input"
            className="flex-1 min-h-[40px] max-h-[120px] resize-none"
          />
          <Button
            size="icon"
            onClick={handleSend}
            disabled={!canSend}
            aria-label="Send message"
            className="size-9 rounded-full flex-shrink-0"
          >
            {isSending && !isStreaming ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </div>
      </div>
    </div>
  )
}
